import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  DynamicFormbuilderService,
  FieldDef,
} from '@domgen/dgx-fe-dynamic-form-builder';
import { CmsFormField } from '@domgen/dgx-fe-business-models';
import { OfferRenewalFormConfigService } from './offer-renewal-form-config.service';
import { FormGroup } from '@angular/forms';
import { OfferRenewalFormSubmit } from '@common/util-models';

export interface OfferRenewalFormState {
  formBuilderConfig: FieldDef[];
  cmsFormData: CmsFormField[];
  form: FormGroup;
  validate: boolean;
  referenceType: string | undefined;
  submittedForm: OfferRenewalFormSubmit | undefined;
  prefilledForm: OfferRenewalFormSubmit | undefined | null;
}

@Injectable()
export class OfferRenewalFormStateService extends ComponentStore<OfferRenewalFormState> {
  readonly formBuilderConfig$ = this.select(
    (state: OfferRenewalFormState) => state.formBuilderConfig
  );

  readonly formGroup$ = this.select(
    (state: OfferRenewalFormState) => state.form
  );

  readonly cmsFormData$ = this.select(
    (state: OfferRenewalFormState) => state.cmsFormData
  ).pipe(filter((cmsData) => !!cmsData.length));

  readonly referenceType$ = this.select(
    (state: OfferRenewalFormState) => state.referenceType
  ).pipe(filter((referenceType) => referenceType !== undefined));

  readonly validate$ = this.select(
    (state: OfferRenewalFormState) => state.validate
  );

  readonly submittedForm$ = this.select(
    (state: OfferRenewalFormState) => state.submittedForm
  ).pipe(filter((submittedForm) => !!submittedForm));

  readonly prefilledForm$ = this.select(
    (state: OfferRenewalFormState) => state.prefilledForm
  ).pipe(
    filter((formData) => formData !== undefined)
  ) as Observable<OfferRenewalFormSubmit | null>;

  validatedSubmit$ = this.select(
    this.submittedForm$,
    (submittedForm) =>
      ({
        ...submittedForm,
      } as OfferRenewalFormSubmit)
  );
  readonly setCmsFormData = this.updater(
    (state: OfferRenewalFormState, value: CmsFormField[]) => ({
      ...state,
      cmsFormData: value,
    })
  );

  readonly updateSubmittedForm = this.updater(
    (state: OfferRenewalFormState, submittedForm: OfferRenewalFormSubmit) => ({
      ...state,
      submittedForm,
    })
  );

  readonly updateFormValue = this.updater(
    (state: OfferRenewalFormState, value: unknown) => ({
      ...state,
      formValue: value as OfferRenewalFormSubmit,
    })
  );

  readonly setReferenceType = this.updater(
    (state: OfferRenewalFormState, value: string | undefined) => ({
      ...state,
      referenceType: value,
    })
  );

  readonly setPrefilledFormData = this.updater(
    (state: OfferRenewalFormState, value: OfferRenewalFormSubmit | null) => ({
      ...state,
      prefilledForm: value,
    })
  );

  readonly validate = this.updater(
    (state: OfferRenewalFormState, validate: boolean) => ({
      ...state,
      validate,
    })
  );

  readonly vm$ = this.select(
    this.formBuilderConfig$,
    this.formGroup$,
    this.validate$,
    (fieldDef, formGroup, validate) => ({
      fieldDef,
      formGroup,
      validate,
    })
  );

  // *** Updater Sources ****
  private fieldDefinitionUpdater$ = combineLatest([
    this.cmsFormData$,
    this.referenceType$,
    this.prefilledForm$,
  ]).pipe(
    map(([cmsData, referenceType, formData]) => {
      return this.offerRenewalFormConfig.getFormbuilderConfig(
        cmsData,
        referenceType,
        formData
      );
    })
  );

  private readonly cmsFieldDefinitionUpdater = this.updater(
    (state: OfferRenewalFormState, value: FieldDef[]) => ({
      ...state,
      formBuilderConfig: value,
    })
  )(this.fieldDefinitionUpdater$);

  private formGroupUpdater$ = this.formBuilderConfig$.pipe(
    switchMap((formBuilderConfig) =>
      of(formBuilderConfig).pipe(
        map((conf) => this.dynamicFormBuilder.generateFormGroup(conf))
      )
    )
  );

  private readonly formGroupUpdater = this.updater(
    (state: OfferRenewalFormState, value: FormGroup) => ({
      ...state,
      form: value,
    })
  )(this.formGroupUpdater$);

  constructor(
    private offerRenewalFormConfig: OfferRenewalFormConfigService,
    private dynamicFormBuilder: DynamicFormbuilderService
  ) {
    super({
      formBuilderConfig: [],
      cmsFormData: [],
      form: new FormGroup({}),
      validate: false,
      referenceType: undefined,
      submittedForm: undefined,
      prefilledForm: undefined,
    });
  }
}
